import React, { useLayoutEffect, useRef, useState } from "react";
import { niceAxis, periodLabel, metricValue, money } from "./reportsFormat.mjs";

/*
 * THE BAR CHART, shared by the drill-down and by every chart widget in the grid.
 *
 * It draws one tile's series at one granularity, both of which arrive on the
 * tile (`series_by.weekly` / `series_by.monthly`, cut to the page's scope by
 * src/reports/metrics.js), so switching granularity is a re-render, not a fetch.
 *
 * What a bar can be:
 *   whole         a complete period, yellow
 *   partial       a period the scope clips, or a cohort still ageing: hatched,
 *                 its value greyed, and left out of the average
 *   current       the period now running: what has happened so far in yellow,
 *                 and the projection to the period's end stacked on it in grey
 *   unknown       no value (a rate below its base, a week with no spend): an
 *                 empty slot and a dash, never a zero bar
 *
 * The line across it is the average of the complete periods for a count, and
 * the scope's own figure for anything else, where an average of bars would
 * weigh a week of two leads the same as a week of twenty.
 */

const AXIS_TICKS = 4;
const LABEL_STRIP = 26; // the period-label strip under the bars

function tickText(value, format) {
  if (format === "percent") return `${value}%`;
  if (format === "currency") return money(value);
  return String(value);
}

/** The series, the reference line and the current point, for one tile at one granularity. */
export function chartFacts(tile, granularity) {
  const series = (tile.series_by && tile.series_by[granularity]) || tile.series || [];
  const unit = granularity === "weekly" ? "weeks" : "months";
  let reference = null;
  let referenceLabel = "";
  let referenceBasis = "";
  if (tile.has_members) {
    const complete = series.filter((p) => !p.partial && p.count != null);
    if (complete.length) {
      reference = Math.round((complete.reduce((a, p) => a + p.count, 0) / complete.length) * 10) / 10;
      referenceLabel = `avg ${reference}`;
      referenceBasis = `mean of the ${complete.length} complete ${unit}, excluding partial ones`;
    }
  } else if (tile.reference != null) {
    reference = tile.reference;
    referenceLabel = `all ${metricValue(reference, tile.format)}`;
    referenceBasis = "the figure for the whole date range";
  }
  const current = series.find((p) => p.current) || null;
  return { series, reference, referenceLabel, referenceBasis, current };
}

export default function ReportsChart({ tile, granularity, height = 236, onSelectPeriod }) {
  const { series, reference, referenceLabel, referenceBasis } = chartFacts(tile, granularity);
  const plotHeight = height - LABEL_STRIP - 32; // room above the tallest bar for its value

  // Thin the labels when the bars get narrow: a 53-week year cannot print 53 keys.
  const plotRef = useRef(null);
  const [width, setWidth] = useState(800);
  useLayoutEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const slot = width / Math.max(1, series.length);
  const keyEvery = Math.max(1, Math.ceil(44 / slot));
  const showValues = slot >= 22;

  const peak = Math.max(
    0,
    ...series.map((p) => Math.max(p.count ?? 0, p.projection ? p.projection.projected : 0)),
    reference ?? 0
  );
  const axis = niceAxis(tile.format === "count" ? peak : Math.ceil(peak), AXIS_TICKS);
  const px = (v) => Math.round((Math.max(0, v) / axis.max) * plotHeight);
  const canOpen = !!tile.has_members && typeof onSelectPeriod === "function";

  return (
    <div className="crm-chart">
      <div className="crm-drill__body">
        <div className="crm-drill__axis" style={{ height }}>
          {axis.ticks.map((tick) => (
            <span key={tick} className="crm-num">{tickText(tick, tile.format)}</span>
          ))}
        </div>

        <div className="crm-drill__plot" style={{ height }} ref={plotRef}>
          <div className="crm-drill__grid">
            {axis.ticks.map((tick, i) => (
              <div key={tick} className={i === axis.ticks.length - 1 ? "is-baseline" : ""} />
            ))}
          </div>

          {reference != null && reference > 0 && (
            <div
              className="crm-drill__avg"
              style={{ bottom: `${LABEL_STRIP + px(reference)}px` }}
              title={referenceBasis}
            >
              <span className="crm-num">{referenceLabel}</span>
            </div>
          )}

          <div className={`crm-drill__bars${series.length > 20 ? " is-dense" : ""}`}>
            {series.map((point, i) => {
              const known = point.count != null && point.known !== false;
              const projection = point.current && point.projection ? point.projection : null;
              const hatched = point.partial && !projection;
              const title = !known
                ? `${periodLabel(point.key)}: no value`
                : projection
                  ? `${periodLabel(point.key)}: ${point.count} so far, heading for ${projection.projected}. ${projection.basis}`
                  : `${periodLabel(point.key)}: ${metricValue(point.count, tile.format)}` +
                    (point.partial ? ` (partial, ${point.days_elapsed} of ${point.days_in_period} days)` : "") +
                    (point.denominator != null ? ` · ${point.numerator} of ${point.denominator}` : "") +
                    (point.n != null ? ` · n=${point.n}` : "") +
                    (point.spend != null ? ` · $${point.spend.toLocaleString()} over ${point.leads} leads` : "");

              const bar = (
                <>
                  {showValues && (
                    <span className={`crm-drill__barval crm-num${hatched || !known ? " is-partial" : ""}`}>
                      {known ? metricValue(point.count, tile.format) : "—"}
                      {projection && <span className="crm-drill__proj-val"> → {projection.projected}</span>}
                    </span>
                  )}
                  {projection && projection.projected > point.count && (
                    <div
                      className="crm-drill__bar crm-drill__bar--proj"
                      style={{ height: `${px(projection.projected) - px(point.count)}px` }}
                    />
                  )}
                  {known && (
                    <div
                      className={`crm-drill__bar${hatched ? " is-partial" : ""}${projection ? " is-current" : ""}`}
                      style={{ height: `${px(point.count)}px` }}
                    />
                  )}
                  <span className={`crm-drill__barkey crm-num${point.partial ? " is-partial" : ""}`}>
                    {i % keyEvery === 0 || i === series.length - 1 ? periodLabel(point.key) : ""}
                  </span>
                </>
              );

              // A bar opens the companies counted IN THAT PERIOD — only for a
              // metric with a member list, and never for an empty period.
              const openable = canOpen && known && point.count > 0 && point.from_date;
              if (!openable) {
                return <div key={point.key} className="crm-drill__slot" title={title}>{bar}</div>;
              }
              return (
                <button
                  type="button"
                  key={point.key}
                  className="crm-drill__slot is-openable"
                  title={`${title} · click for the companies`}
                  onClick={() =>
                    onSelectPeriod({
                      from: point.from_date,
                      to: point.to_date,
                      label: `${tile.label} · ${periodLabel(point.key)}`,
                    })
                  }
                >
                  {bar}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
